import { useClubApi } from "../../lib/clubScope";
import { useEffect, useState, type FormEvent } from "react";

import {
  ApiError,
  type ClubBookingRules,
  type ClubInfo,
  type ClubJoinMode,
  type ClubMembershipPayer,
  type ClubSettings,
} from "../../lib/api";

type RuleKey =
  | "max_advance_days"
  | "min_advance_hours"
  | "max_active_reservations"
  | "max_per_day"
  | "max_per_week"
  | "max_consecutive_blocks"
  | "cancel_hours"
  | "prime_max_per_week";

const RULE_KEYS: RuleKey[] = [
  "max_advance_days",
  "min_advance_hours",
  "max_active_reservations",
  "max_per_day",
  "max_per_week",
  "max_consecutive_blocks",
  "cancel_hours",
  "prime_max_per_week",
];

const WEEKDAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

interface PrimeDay {
  enabled: boolean;
  start: string;
  end: string;
  extra: Array<[string, string]>;
}

function rulesToPrime(primeJson: ClubBookingRules["prime_time_json"]): PrimeDay[] {
  const days: PrimeDay[] = Array.from({ length: 7 }, () => ({ enabled: false, start: "18:00", end: "22:00", extra: [] }));
  Object.entries(primeJson ?? {}).forEach(([weekday, windows]) => {
    const idx = Number(weekday);
    if (idx >= 0 && idx <= 6 && windows.length > 0) {
      days[idx] = { enabled: true, start: windows[0][0], end: windows[0][1], extra: windows.slice(1) };
    }
  });
  return days;
}

function primeToJson(days: PrimeDay[]): Record<string, Array<[string, string]>> | null {
  const out: Record<string, Array<[string, string]>> = {};
  days.forEach((day, idx) => {
    if (day.enabled && day.start && day.end) out[String(idx)] = [[day.start, day.end], ...day.extra];
  });
  return Object.keys(out).length > 0 ? out : null;
}

type ClubFormKey =
  | "name"
  | "contact_name"
  | "contact_phone"
  | "contact_email"
  | "address_line"
  | "neighborhood"
  | "city"
  | "state"
  | "google_maps_url";
type ClubForm = Record<ClubFormKey, string>;

function clubToForm(club: ClubInfo): ClubForm {
  return {
    name: club.name,
    contact_name: club.contact_name ?? "",
    contact_phone: club.contact_phone ?? "",
    contact_email: club.contact_email ?? "",
    address_line: club.address_line ?? "",
    neighborhood: club.neighborhood ?? "",
    city: club.city ?? "",
    state: club.state ?? "",
    google_maps_url: club.google_maps_url ?? "",
  };
}

const CLUB_FIELDS: Array<{ key: ClubFormKey; label: string; type?: string; placeholder?: string; required?: boolean }> = [
  { key: "name", label: "Nombre", required: true },
  { key: "contact_name", label: "Contacto" },
  { key: "contact_phone", label: "Teléfono", type: "tel" },
  { key: "contact_email", label: "Email", type: "email" },
  { key: "address_line", label: "Dirección", placeholder: "Calle, número, referencia" },
  { key: "neighborhood", label: "Colonia" },
  { key: "city", label: "Ciudad" },
  { key: "state", label: "Estado" },
  { key: "google_maps_url", label: "Link de Google Maps", type: "url", placeholder: "https://maps.google.com/..." },
];

const RULE_SELECTS: Array<{ key: RuleKey; label: string; options: number[]; unit: (n: number) => string; off: string }> = [
  {
    key: "max_advance_days",
    label: "Reservar con anticipación de",
    options: [1, 2, 3, 5, 7, 14, 30],
    unit: (n) => (n === 1 ? "1 día" : `${n} días`),
    off: "Sin límite",
  },
  {
    key: "min_advance_hours",
    label: "Reservar con mínimo",
    options: [1, 2, 3, 6, 12, 24],
    unit: (n) => (n === 1 ? "1 hora antes" : `${n} horas antes`),
    off: "Sin mínimo",
  },
  {
    key: "max_active_reservations",
    label: "Reservas activas a la vez",
    options: [1, 2, 3, 4, 5],
    unit: (n) => (n === 1 ? "1 reserva" : `${n} reservas`),
    off: "Sin límite",
  },
  {
    key: "max_per_day",
    label: "Máximo por día",
    options: [1, 2, 3, 4],
    unit: (n) => (n === 1 ? "1 reserva" : `${n} reservas`),
    off: "Sin límite",
  },
  {
    key: "max_per_week",
    label: "Máximo por semana",
    options: [1, 2, 3, 4, 5, 7, 10],
    unit: (n) => (n === 1 ? "1 reserva" : `${n} reservas`),
    off: "Sin límite",
  },
  {
    key: "cancel_hours",
    label: "Cancelar sin penalización hasta",
    options: [1, 2, 4, 6, 12, 24, 48],
    unit: (n) => (n === 1 ? "1 hora antes" : `${n} horas antes`),
    off: "Sin regla",
  },
  {
    key: "max_consecutive_blocks",
    label: "Turnos seguidos en la misma cancha",
    options: [1, 2, 3, 4],
    unit: (n) => (n === 1 ? "1 turno" : `${n} turnos`),
    off: "Sin límite",
  },
  {
    key: "prime_max_per_week",
    label: "Reservas en hora pico por semana",
    options: [1, 2, 3, 4, 5, 7],
    unit: (n) => (n === 1 ? "1 reserva" : `${n} reservas`),
    off: "Sin límite",
  },
];

const GRACE_OPTIONS = [0, 3, 5, 7, 10, 15];

const PAYER_OPTIONS: Array<{ value: ClubMembershipPayer; title: string; hint: string }> = [
  { value: "player", title: "La paga cada jugador", hint: "$99 por jugador, cobro automático mensual con Mercado Pago." },
  { value: "club", title: "La paga el club", hint: "Una cuenta mensual por todos los socios activos." },
  { value: "none", title: "Sin cobro", hint: "Solo la activa el superadmin (convenios o prueba)." },
];

const JOIN_OPTIONS: Array<{ value: ClubJoinMode; title: string }> = [
  { value: "request", title: "Solo con solicitud y aprobación" },
  { value: "invite", title: "Solo con link de invitación" },
  { value: "both", title: "Ambas" },
];

interface Props {
  token: string;
  club: ClubInfo;
  isSuperadmin: boolean;
  rulesVersion: number;
  onSaved: (message: string) => void;
  onClubUpdated: (club: ClubInfo) => void;
}

export default function ClubConfigPanel({ token, club, isSuperadmin, rulesVersion, onSaved, onClubUpdated }: Props) {
  const clubApi = useClubApi();
  const [settings, setSettings] = useState<ClubSettings | null>(null);
  const [clubForm, setClubForm] = useState<ClubForm>(() => clubToForm(club));
  const [ruleDraft, setRuleDraft] = useState<Record<RuleKey, string>>(
    () => Object.fromEntries(RULE_KEYS.map((k) => [k, ""])) as Record<RuleKey, string>,
  );
  const [primeDraft, setPrimeDraft] = useState<PrimeDay[]>(() => rulesToPrime(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([clubApi.getSettings(token), clubApi.getRules(token)])
      .then(([settingsResp, rulesResp]) => {
        if (!alive) return;
        setSettings(settingsResp.data.settings);
        const r = rulesResp.data.rules;
        setRuleDraft(
          Object.fromEntries(RULE_KEYS.map((k) => [k, r[k] == null ? "" : String(r[k])])) as Record<RuleKey, string>,
        );
        setPrimeDraft(rulesToPrime(r.prime_time_json));
      })
      .catch((err) => {
        if (alive) setError(err instanceof ApiError ? err.message : "No pudimos cargar la configuración.");
      });
    return () => {
      alive = false;
    };
  }, [token, rulesVersion]);

  useEffect(() => {
    setClubForm(clubToForm(club));
  }, [club]);

  if (!settings) {
    return (
      <section className="club-panel__card">
        {error ? <p className="form-error" role="alert">{error}</p> : <p className="club-panel__muted">Cargando configuración…</p>}
      </section>
    );
  }

  const update = <K extends keyof ClubSettings>(key: K, value: ClubSettings[K]) =>
    setSettings((current) => (current ? { ...current, [key]: value } : current));

  const inviteUrl = `${window.location.origin}/club-invite/${club.slug}/${club.invite_token}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      onSaved("Link de invitación copiado.");
    } catch {
      onSaved(inviteUrl);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const trimmed = (key: ClubFormKey) => clubForm[key].trim() || null;
      const rulesPayload: Partial<ClubBookingRules> = { prime_time_json: primeToJson(primeDraft) };
      RULE_KEYS.forEach((k) => {
        rulesPayload[k] = ruleDraft[k] === "" ? null : Number(ruleDraft[k]);
      });
      const [clubResp, settingsResp] = await Promise.all([
        clubApi.updateMe(token, {
          name: clubForm.name.trim(),
          contact_name: trimmed("contact_name"),
          contact_phone: trimmed("contact_phone"),
          contact_email: trimmed("contact_email"),
          address_line: trimmed("address_line"),
          neighborhood: trimmed("neighborhood"),
          city: trimmed("city"),
          state: trimmed("state"),
          google_maps_url: trimmed("google_maps_url"),
        }),
        clubApi.updateSettings(token, {
          membership_payer: settings.membership_payer,
          grace_days: settings.grace_days,
          after_grace: settings.after_grace,
          club_fee_mode: settings.club_fee_mode,
          join_mode: settings.join_mode,
          invite_requires_approval: settings.invite_requires_approval,
          reservation_confirmation: settings.reservation_confirmation,
        }),
        clubApi.updateRules(token, rulesPayload),
      ]);
      setSettings(settingsResp.data.settings);
      onClubUpdated(clubResp.data.club);
      onSaved("Configuración guardada.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo guardar la configuración.");
    } finally {
      setSaving(false);
    }
  }

  const graceOptions = GRACE_OPTIONS.includes(settings.grace_days)
    ? GRACE_OPTIONS
    : [...GRACE_OPTIONS, settings.grace_days].sort((a, b) => a - b);

  return (
    <form className="club-config" onSubmit={handleSubmit} aria-label="Configuración del club">
      <section className="club-panel__hero">
        <div>
          <p className="club-panel__eyebrow">{club.name}</p>
          <h1>Configuración del club</h1>
          <p>Elige cómo funciona tu club en Torneos123.</p>
        </div>
      </section>

      {error && <p className="form-error" role="alert">{error}</p>}

      <section className="club-config__section">
        <h2 className="club-config__title">Datos del club</h2>
        <div className="club-config__fields">
          {CLUB_FIELDS.map((field) => (
            <label key={field.key}>
              {field.label}
              <input
                type={field.type ?? "text"}
                required={field.required}
                value={clubForm[field.key]}
                placeholder={field.placeholder}
                disabled={saving}
                onChange={(e) => setClubForm((f) => ({ ...f, [field.key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="club-config__section">
        <h2 className="club-config__title">Membresía Torneos123 ($99 al mes)</h2>
        <div className="club-config__options" role="group" aria-label="Quién paga la membresía">
          {PAYER_OPTIONS.map((option) => {
            const locked = option.value === "none" && !isSuperadmin && settings.membership_payer !== "none";
            const selected = settings.membership_payer === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className="club-config__option"
                aria-pressed={selected}
                disabled={locked || saving}
                onClick={() => update("membership_payer", option.value)}
              >
                <strong>
                  {option.title}
                  {selected ? " ✓" : ""}
                </strong>
                <span>{option.hint}</span>
              </button>
            );
          })}
        </div>
        <div className="club-config__inline">
          <label>
            Días de gracia si no paga
            <select
              className="t123-select"
              value={settings.grace_days}
              disabled={saving}
              onChange={(e) => update("grace_days", Number(e.target.value))}
            >
              {graceOptions.map((days) => (
                <option key={days} value={days}>
                  {days === 0 ? "Sin gracia" : days === 1 ? "1 día" : `${days} días`}
                </option>
              ))}
            </select>
          </label>
          <label>
            Después de la gracia
            <select
              className="t123-select"
              value={settings.after_grace}
              disabled={saving}
              onChange={(e) => update("after_grace", e.target.value as ClubSettings["after_grace"])}
            >
              <option value="no_booking">No puede reservar (sigue siendo socio)</option>
              <option value="suspended">Queda suspendido</option>
            </select>
          </label>
        </div>
        <p className="club-config__note">El cobro automático se activa en la siguiente etapa. Por ahora solo guardamos tu elección.</p>
      </section>

      <section className="club-config__section">
        <h2 className="club-config__title">Cuota del club</h2>
        <div className="club-config__options club-config__options--two" role="group" aria-label="Cuota del club">
          <button
            type="button"
            className="club-config__option"
            aria-pressed={settings.club_fee_mode === "external"}
            disabled={saving}
            onClick={() => update("club_fee_mode", "external")}
          >
            <strong>La cobro por fuera{settings.club_fee_mode === "external" ? " ✓" : ""}</strong>
            <span>Torneos123 no interviene.</span>
          </button>
          <button type="button" className="club-config__option" aria-pressed={false} disabled>
            <strong>Cobrarla en la app</strong>
            <span>Próximamente</span>
          </button>
        </div>
      </section>

      <section className="club-config__section">
        <h2 className="club-config__title">Cómo entran los socios</h2>
        <div className="club-config__options" role="group" aria-label="Cómo entran los socios">
          {JOIN_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="club-config__option"
              aria-pressed={settings.join_mode === option.value}
              disabled={saving}
              onClick={() => update("join_mode", option.value)}
            >
              <strong>
                {option.title}
                {settings.join_mode === option.value ? " ✓" : ""}
              </strong>
            </button>
          ))}
        </div>
        {settings.join_mode !== "request" && (
          <>
            <label className="club-config__check">
              <input
                type="checkbox"
                checked={settings.invite_requires_approval}
                disabled={saving}
                onChange={(e) => update("invite_requires_approval", e.target.checked)}
              />
              Quien entra con link también necesita mi aprobación
            </label>
            <div className="club-config__invite">
              <span>{inviteUrl}</span>
              <button type="button" className="btn btn--compact btn--ghost" onClick={handleCopy}>
                Copiar link
              </button>
            </div>
          </>
        )}
      </section>

      <section className="club-config__section">
        <h2 className="club-config__title">Reglas de reserva</h2>
        <div className="club-config__fields">
          {RULE_SELECTS.map((rule) => {
            const current = ruleDraft[rule.key];
            const options =
              current !== "" && !rule.options.includes(Number(current))
                ? [...rule.options, Number(current)].sort((a, b) => a - b)
                : rule.options;
            return (
              <label key={rule.key}>
                {rule.label}
                <select
              className="t123-select"
                  value={current}
                  disabled={saving}
                  onChange={(e) => setRuleDraft((d) => ({ ...d, [rule.key]: e.target.value }))}
                >
                  <option value="">{rule.off}</option>
                  {options.map((n) => (
                    <option key={n} value={String(n)}>
                      {rule.unit(n)}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
          <label>
            Confirmación
            <select
              className="t123-select"
              value={settings.reservation_confirmation}
              disabled={saving}
              onChange={(e) =>
                update("reservation_confirmation", e.target.value as ClubSettings["reservation_confirmation"])
              }
            >
              <option value="auto">Se confirman solas</option>
              <option value="club_approves">El club aprueba cada una</option>
            </select>
          </label>
        </div>
        <div className="club-config__prime" role="group" aria-label="Horas pico">
          <p className="club-config__subtitle">Horas pico</p>
          <p className="club-config__hint">El límite semanal de hora pico solo cuenta reservas dentro de estas ventanas.</p>
          {primeDraft.map((day, idx) => (
            <div key={idx} className={`club-config__prime-row${day.enabled ? "" : " club-config__prime-row--off"}`}>
              <label className="club-config__check">
                <input
                  type="checkbox"
                  checked={day.enabled}
                  disabled={saving}
                  onChange={(e) =>
                    setPrimeDraft((d) => d.map((x, i) => (i === idx ? { ...x, enabled: e.target.checked } : x)))
                  }
                />
                {WEEKDAYS[idx]}
              </label>
              <input
                type="time"
                aria-label={`${WEEKDAYS[idx]} desde`}
                value={day.start}
                disabled={saving || !day.enabled}
                onChange={(e) => setPrimeDraft((d) => d.map((x, i) => (i === idx ? { ...x, start: e.target.value } : x)))}
              />
              <input
                type="time"
                aria-label={`${WEEKDAYS[idx]} hasta`}
                value={day.end}
                disabled={saving || !day.enabled}
                onChange={(e) => setPrimeDraft((d) => d.map((x, i) => (i === idx ? { ...x, end: e.target.value } : x)))}
              />
            </div>
          ))}
        </div>
        <p className="club-config__hint">Las reglas solo aplican a las reservas que hacen los socios; recepción puede reservar sin restricciones.</p>
      </section>

      <div className="club-config__actions">
        <button type="submit" className="club-panel__lime-btn" disabled={saving}>
          {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </form>
  );
}
